import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Pencil, Settings } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell, AppFooter } from "../components/Shell";
import { BottomNav } from "../components/BottomNav";
import { Avatar } from "../components/Avatar";
import { PhotoUpload } from "../components/PhotoUpload";
import { StatBar } from "../components/StatBar";
import { DeleteAccount } from "../components/DeleteAccount";
import { ResultsEditor } from "../components/Results";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";
import { STATIONS, SEARCH_STATUSES, formatTime, profilePayload } from "../constants/rox";
import { useT, LangToggle, useLabels } from "../i18n";

export default function Profile() {
  const { profile, user, logout, setProfile } = useAuth();
  const { t, fmtDate } = useT();
  const L = useLabels();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (patch) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.put("/profile/me", profilePayload(profile, patch));
      setProfile(data);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const out = async () => {
    await logout();
    navigate("/");
  };

  return (
    <AppShell>
      <TopBar back={false} right={<button data-testid="logout-button" onClick={out} className="text-rox-muted hover:text-rox-orange" aria-label={t("logout")}><LogOut size={20} /></button>} />
      <main className="px-5 pb-28 pt-2 stagger lg:px-8 lg:pb-16" data-testid="profile-page">
        <div className="flex items-center justify-between"><p className="eyebrow text-rox-orange">{t("profile")}</p><LangToggle className="lg:hidden" /></div>
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          <div>
            <div className="mt-2 flex items-center gap-4">
              <Avatar name={profile.first_name} src={profile.photo_url} size={72} ring={false} testId="profile-avatar" />
              <div className="min-w-0">
                <h1 className="h-display text-4xl" data-testid="profile-name">{profile.first_name}{profile.age ? `, ${t("age_years", profile.age)}` : ""}</h1>
                <p className="truncate text-sm text-rox-muted">{profile.last_name ? `${profile.last_name} · ` : ""}{profile.city} · {user.email}</p>
              </div>
            </div>
            <div className="panel mt-4 p-3">
              <PhotoUpload name={profile.first_name} photoUrl={profile.photo_url} size={56} onUploaded={(url) => setProfile((p) => ({ ...p, photo_url: url }))} />
            </div>

            <section className="panel mt-4 p-4" data-testid="status-section">
              <p className="eyebrow">{t("status_title")}</p>
              <div className="mt-3 grid gap-2">
                {SEARCH_STATUSES.map((s) => (
                  <button key={s} type="button" data-testid={`status-option-${s}`} data-active={profile.search_status === s} disabled={busy} onClick={() => profile.search_status !== s && save({ search_status: s })}
                    className={`option-card text-sm ${profile.search_status === s ? "" : "hover:border-rox-off/50"}`}>
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${profile.search_status === s ? "bg-rox-orange" : "bg-rox-line"}`} />{L.status(s)}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-rox-muted">{t("status_hint")}</p>
            </section>
            {error && <p className="mt-3 text-sm text-rox-orange" data-testid="profile-error">{error}</p>}

            <div className="mt-6 grid grid-cols-2 gap-2">
              {[[t("race"), profile.race_name, profile.race_date ? fmtDate(profile.race_date) : profile.race !== "other" ? t("race_date_tbc") : ""], [t("format"), L.category(profile), ""], [t("level"), L.level(profile), ""], [t("chrono"), formatTime(profile.target_time), L.goal(profile)]].map(([k, v, sub]) => (
                <div key={k} className="panel p-3">
                  <p className="eyebrow">{k}</p>
                  <p className="mt-1 font-display text-base uppercase leading-tight">{v}</p>
                  {sub && <p className="mt-0.5 text-[11px] text-rox-muted">{sub}</p>}
                </div>
              ))}
            </div>
            <p className="mt-3 text-sm"><span className="text-rox-orange">■</span> {L.strengths(profile)}</p>
            <p className="mt-1 text-xs text-rox-muted">
              {L.availability(profile).join(" · ")}
              {profile.instagram ? ` · @${profile.instagram}` : ""}
            </p>
            {profile.bio && <p className="mt-4 border-l-2 border-rox-orange pl-4 text-sm text-rox-off/90" data-testid="profile-bio">« {profile.bio} »</p>}

            <Link to="/onboarding" data-testid="edit-profile" className="mt-5 flex items-center justify-between rounded-[4px] border border-rox-line px-4 py-3 font-display text-sm uppercase tracking-wider hover:border-rox-orange hover:text-rox-orange">
              {t("edit_profile")} <Pencil size={16} />
            </Link>
            {user.is_admin && (
              <Link to="/admin/courses" data-testid="admin-link" className="mt-3 flex items-center justify-between rounded-[4px] border border-rox-orange/50 px-4 py-3 font-display text-sm uppercase tracking-wider text-rox-orange hover:bg-rox-orange/10">
                {t("admin_link")} <Settings size={16} />
              </Link>
            )}
          </div>

          <div>
            <ResultsEditor results={profile.results || []} onSave={(results) => save({ results })} busy={busy} />
            <h2 className="h-display mt-8 text-3xl">{t("my_stations")}</h2>
            <div className="panel mt-4 space-y-2.5 p-4">
              {STATIONS.map((s, i) => <StatBar key={s.key} label={L.station(s.key)} value={profile.scores[s.key]} delay={i * 50} />)}
            </div>
            <DeleteAccount />
          </div>
        </div>
      </main>
      <AppFooter />
      <BottomNav />
    </AppShell>
  );
}
